import { flow, isNull, set } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { useAppDispatch } from '../../../../hooks/react-redux';
import { IconButton } from '../../../shared/button/IconButton';
import { Filter } from '../../../shared/icons';
import { Position } from '../../../shared/modal/PositionModal';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { Toggle } from '../../../shared/toggle';
import styles from '../Clients.module.scss';
import { Client, ClientOpinion, updateClientDetails } from '../store';
import { JurisdictionFilterModal } from '../JurisdictionFilterModal';

const { white, lightestGrey } = styles;

interface ClientOpinionsProps {
    clientOpinions: ClientOpinion[];
}

export const ClientOpinions: React.FC<ClientOpinionsProps> = ({ clientOpinions }) => {
    const [jurisdictionFilterModalPosition, setJurisdictionFilterModalPosition] = useState<Position | null>(null);
    const [jurisdictionFilter, setJurisdictionFilter] = useState<string>('');
    const dispatch = useAppDispatch();

    const updateClientProperties = useCallback((key: keyof Client, value: string | number[] | ClientOpinion[] | number | null) => dispatch(updateClientDetails(key, value)), [dispatch]);
    const filteredClientOpinions = useMemo(() => clientOpinions.filter(({ jurisdiction }) => jurisdiction.toLowerCase().includes(jurisdictionFilter.toLowerCase())).sort((a, b) => a.jurisdiction.localeCompare(b.jurisdiction)), [clientOpinions, jurisdictionFilter]);

    const openJurisdictionFilter = (x: number, y: number) => setJurisdictionFilterModalPosition({ x, y });
    const closeJurisdictionFilter = () => setJurisdictionFilterModalPosition(null);

    const updateClientOpinions = (checked: boolean, jurisdiction: string, focus: string, property: keyof ClientOpinion) => {
        const updatedOpinions: ClientOpinion[] = clientOpinions.map(opinion => {
            if (opinion.jurisdiction === jurisdiction && opinion.focus === focus) {
                return set(property, checked, opinion);
            }
            return opinion;
        });
        updateClientProperties('clientOpinions', updatedOpinions);
    };

    const { totalIsdaNetting, totalClientReliance, totalIsdaCollateral, totalIcma, totalIsla } = useMemo(() => clientOpinions.reduce((acc, cur) => flow(
        set('totalIsdaNetting', acc.totalIsdaNetting + (cur.isdaNetting ? 1 : 0)),
        set('totalClientReliance', acc.totalClientReliance + (cur.clientReliance ? 1 : 0)),
        set('totalIsdaCollateral', acc.totalIsdaCollateral + (cur.isdaCollateral ? 1 : 0)),
        set('totalIcma', acc.totalIcma + (cur.icma ? 1 : 0)),
        set('totalIsla', acc.totalIsla + (cur.isla ? 1 : 0))
    )(acc), { totalIsdaNetting: 0, totalClientReliance: 0, totalIsdaCollateral: 0, totalIcma: 0, totalIsla: 0 }), [clientOpinions]);

    const totalOpinions = useMemo(() => totalIcma + totalIsdaNetting + totalIsdaCollateral + totalIsla, [totalIcma, totalIsdaNetting, totalIsdaCollateral, totalIsla]);

    const getOpinionColumnHeader = useCallback((column: string) => {
        switch (column) {
            case 'ISDA Netting':
                return `ISDA Netting (${totalIsdaNetting})`;
            case 'Client Reliance':
                return `Client Reliance (${totalClientReliance})`;
            case 'ISDA Collateral':
                return `ISDA Collateral (${totalIsdaCollateral})`;
            case 'ICMA':
                return `ICMA (${totalIcma})`;
            case 'ISLA':
                return `ISLA (${totalIsla})`;
            case 'Jurisdiction':
            case 'Focus':
            default:
                return column;
        }
    }, [totalIcma, totalIsdaNetting, totalClientReliance, totalIsdaCollateral, totalIsla]);

    const opinionHeaders = [{ name: 'Jurisdiction', width: '20%', justifyContent: 'flex-start', showFilter: true }, { name: 'Focus', width: '20%', justifyContent: 'flex-start' }, { name: 'ISDA Netting', width: '12%', justifyContent: 'center' }, { name: 'Client Reliance', width: '12%', justifyContent: 'center' }, { name: 'ISDA Collateral', width: '12%', justifyContent: 'center' }, { name: 'ICMA', width: '12%', justifyContent: 'center' }, { name: 'ISLA', width: '12%', justifyContent: 'center' }];

    return (
        <>
            <div className={styles.clientModalContentWrapper}>
                <div className={styles.clientModalHeader}>
                    <div className={styles.clientModalTitle}>Opinions Available to Client ({totalOpinions})</div>
                </div>
                <>
                    <div className={styles.tableHeader}>
                        {opinionHeaders.map(({ name, width, justifyContent, showFilter }) => (
                            <div className={styles.headerCell} key={name} style={{ width, justifyContent }}>
                                <div>{getOpinionColumnHeader(name)}</div>
                                {showFilter && <IconButton icon={Filter} onClick={e => openJurisdictionFilter(e.clientX, e.clientY)} color={white} fontSize={20} margin='0 0 0 10px' showIndicator={!!jurisdictionFilter.length} />}
                            </div>
                        ))}
                    </div>
                    <div className={styles.tableWrapper}>
                        <Scrollable>
                            {filteredClientOpinions.map(({ jurisdiction, focus, isdaNetting, clientReliance, isdaCollateral, isla, icma }, index) => (
                                <div className={styles.tableRow} key={index} style={{ backgroundColor: index % 2 ? lightestGrey : white }}>
                                    <div className={styles.titleCell} style={{ width: '20%' }}>{jurisdiction}</div>
                                    <div className={styles.titleCell} style={{ width: '20%' }}>{focus}</div>
                                    <div className={styles.toggleCell} style={{ width: '12%' }}>
                                        <Toggle
                                            checked={isdaNetting}
                                            onChange={checked => updateClientOpinions(checked, jurisdiction, focus, 'isdaNetting')}
                                        />
                                    </div>
                                    <div className={styles.toggleCell} style={{ width: '12%' }}>
                                        <Toggle
                                            checked={clientReliance}
                                            onChange={checked => updateClientOpinions(checked, jurisdiction, focus, 'clientReliance')}
                                        />
                                    </div>
                                    <div className={styles.toggleCell} style={{ width: '12%' }}>
                                        <Toggle
                                            checked={isdaCollateral}
                                            onChange={checked => updateClientOpinions(checked, jurisdiction, focus, 'isdaCollateral')}
                                        />
                                    </div>
                                    <div className={styles.toggleCell} style={{ width: '12%' }}>
                                        <Toggle
                                            checked={icma}
                                            onChange={checked => updateClientOpinions(checked, jurisdiction, focus, 'icma')}
                                        />
                                    </div>
                                    <div className={styles.toggleCell} style={{ width: '12%' }}>
                                        <Toggle
                                            checked={isla}
                                            onChange={checked => updateClientOpinions(checked, jurisdiction, focus, 'isla')}
                                        />
                                    </div>
                                </div>
                            ))}
                        </Scrollable>
                    </div>
                </>
            </div>
            {!isNull(jurisdictionFilterModalPosition) &&
                <JurisdictionFilterModal
                    closeModal={closeJurisdictionFilter}
                    value={jurisdictionFilter}
                    setFilterValue={setJurisdictionFilter}
                    position={jurisdictionFilterModalPosition}
                />
            }
        </>
    );
};
